import { useCallback, useEffect, useRef, useState } from "react";

import {
  type PaddleEventData,
  type UpdateCheckoutParams,
  type ExtendedPaddle,
  CheckoutEventNames,
} from "@paddle/paddle-js";
import { SubscriptionIntervals } from "@prisma/client";
import { useRouter } from "next/router";
import { useSession } from "next-auth/react";
import { useFeatureFlagVariantKey } from "posthog-js/react";

import {
  FOUNDING_CUSTOM_DATA,
  isFoundingDiscountApplied,
} from "@musicfy/contants/FoundingOffer";
import {
  AdditionalVoicesId,
  PRICE_ID_TO_INTERVAL_MAP,
  PRICE_ID_TO_PLAN_MAP,
  ProductId,
  type TProductId,
  type TPriceId,
} from "@musicfy/contants/Subscriptions";
import { useAnalytics } from "@musicfy/libs/AnalyticsProvider";
import {
  PADDLE_TARGET_FRAME,
  usePaddleContext,
} from "@musicfy/libs/PaddleProvider";
import {
  PaddleScreens,
  type CheckoutEvents,
} from "@musicfy/libs/PaddleProvider/types";
import { api } from "@musicfy/utils";

import {
  BANNER_COPY_FLAG_KEY,
  DISCOUNT_FLAG_KEY,
  INTERVALS_FLAG_KEY,
  pricingVariantsForCheckout,
} from "./usePricingExperiments";

export type StartCheckoutDiscount = { code?: string; id?: string };
export type StartCheckoutOptions = {
  customData?: Record<string, string | number | boolean>;
};

export const usePaddleCheckout = ({
  onLoad,
  onClose,
  onCheckoutCompleted,
  onCheckoutUpdated,
  onPaymentInitiated,
  onError,
}: CheckoutEvents = {}) => {
  const { trackEvent } = useAnalytics();
  const router = useRouter();

  const {
    checkoutContext,
    subscribeToPaddleEvents,
    unsubscribeFromPaddleEvents,
    setActivePaddleScreen,
    origin,
    setOrigin,
  } = usePaddleContext();

  const {
    checkoutData,
    setCheckoutData,
    setCheckoutError,
    isFetchingSubscriptionRef,
    isCheckoutUpdating,
    setIsCheckoutUpdating,
  } = checkoutContext;

  const { data } = useSession();
  const user = data?.user;
  const userRef = useRef(user);
  userRef.current = user;

  // Pricing-experiment variants, read at render and persisted into checkout customData below so the
  // day-35 renewal read can attribute to the buyer's arm. NOTE: useFeatureFlagVariantKey fires
  // PostHog's `$feature_flag_called` (deduped per flag-key per pageload). usePricingExperiments
  // already reads these same flags on the pricing page, so on the normal path this read is a no-op
  // dedupe. On a checkout path that bypasses the pricing page it would be the first read for those
  // flags — confirm the experiment's exposure is defined on the custom "Pricing Experiment Exposed"
  // event (gated on `ready`), not on `$feature_flag_called`, or that path contaminates the denominator.
  const discountVariant = useFeatureFlagVariantKey(DISCOUNT_FLAG_KEY);
  const intervalsVariant = useFeatureFlagVariantKey(INTERVALS_FLAG_KEY);
  const bannerVariant = useFeatureFlagVariantKey(BANNER_COPY_FLAG_KEY);

  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [canCloseCheckout, setCanCloseCheckout] = useState(true);

  const { mutateAsync: checkIsValidDiscountCode } =
    api.subscription.checkIsValidDiscountCode.useMutation();

  const eventsCallback = useCallback(
    (event: PaddleEventData) => {
      switch (event.type) {
        case "checkout.error":
          onError?.(event);
          setCheckoutError(event);
          break;
      }

      if (!event.data) {
        return;
      }

      switch (event.name) {
        case CheckoutEventNames.CHECKOUT_LOADED:
          onLoad?.(event.data);
          setCheckoutData(event.data);
          break;
        case CheckoutEventNames.CHECKOUT_UPDATED:
          onCheckoutUpdated?.(event.data);
          setCheckoutData(event.data);
          setIsCheckoutUpdating(false);
          break;
        case CheckoutEventNames.CHECKOUT_COMPLETED:
          onCheckoutCompleted?.(event.data);
          setCheckoutData(event.data);
          setIsPaymentProcessing(false);
          setCanCloseCheckout(false);

          const priceId = event.data.items[0]?.price_id as TPriceId;
          const plan = PRICE_ID_TO_PLAN_MAP[priceId];
          const interval = PRICE_ID_TO_INTERVAL_MAP[priceId];
          // event.data.totals.total is already a decimal-adjusted amount
          // (Paddle's checkout.js converts it before emitting the event),
          // so it must not be divided by 100 again here.
          const bookedRevenue = event.data.totals.total;

          if (!isFetchingSubscriptionRef.current) {
            isFetchingSubscriptionRef.current = true;
            trackEvent("Subscription Upgrade", {
              plan: plan,
              interval: interval,
              price: bookedRevenue,
              $revenue: bookedRevenue,
              currency: event.data.currency_code,
              origin: origin,
              checkout_provider: "paddle",
            });
          }
          setTimeout(() => {
            router.reload();
            setCanCloseCheckout(false);
          }, 4000);

          break;
        case CheckoutEventNames.CHECKOUT_PAYMENT_INITIATED:
          onPaymentInitiated?.(event.data);
          setCheckoutData(event.data);
          setIsPaymentProcessing(true);
          break;
        case CheckoutEventNames.CHECKOUT_DISCOUNT_REMOVED:
        case CheckoutEventNames.CHECKOUT_DISCOUNT_APPLIED:
          setCheckoutData(event.data);
          break;
        case CheckoutEventNames.CHECKOUT_PAYMENT_FAILED:
          setIsPaymentProcessing(false);
          break;
        case CheckoutEventNames.CHECKOUT_CLOSED:
          onClose?.(undefined);
          setCheckoutData(undefined);
          setCheckoutError(undefined);
          setIsPaymentProcessing(false);
          break;
        default:
          setIsPaymentProcessing(false);
          break;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onCheckoutCompleted,
      onCheckoutUpdated,
      onClose,
      onError,
      onLoad,
      onPaymentInitiated,
      origin,
      trackEvent,
      router,
      setCheckoutData,
      setCheckoutError,
    ]
  );

  useEffect(() => {
    subscribeToPaddleEvents(eventsCallback);

    return () => {
      unsubscribeFromPaddleEvents(eventsCallback);
    };
  }, [eventsCallback, subscribeToPaddleEvents, unsubscribeFromPaddleEvents]);

  const startCheckout = useCallback(
    (
      priceId: TPriceId,
      discount?: StartCheckoutDiscount,
      options?: StartCheckoutOptions
    ) => {
      setActivePaddleScreen(PaddleScreens.CHECKOUT);

      const openWhenReady = (attempt = 0) => {
        const maxAttempts = 50; // ~5s at 100ms
        const currentUser = userRef.current;
        // Inline checkout mounts into `.paddle-container`, which only exists
        // after the CHECKOUT screen commits. Opening before that throws
        // iframe.gateway appendChild / React cross-origin errors.
        const frameReady = !!document.querySelector(`.${PADDLE_TARGET_FRAME}`);
        if (!window.Paddle || !currentUser || !frameReady) {
          if (attempt >= maxAttempts) {
            // Don't leave the user on an infinite checkout loader if Paddle
            // never boots (common locally) or session user isn't ready yet.
            setActivePaddleScreen(null);
            return;
          }
          window.setTimeout(() => openWhenReady(attempt + 1), 100);
          return;
        }

        const items = [
          {
            priceId: priceId,
          },
        ];

        const isFounding = isFoundingDiscountApplied(discount);

        try {
          window.Paddle.Checkout.open({
            customer: {
              email: currentUser.email,
            },
            customData: {
              userId: currentUser.id,
              ...(isFounding ? FOUNDING_CUSTOM_DATA : {}),
              ...options?.customData,
              // Pricing-experiment variants LAST (after options.customData) so a caller cannot
              // override the arm the buyer was actually in — attribution stays trustworthy.
              ...pricingVariantsForCheckout({
                discount: discountVariant,
                intervals: intervalsVariant,
                bannerCopy: bannerVariant,
              }),
              ...(window.tolt_referral && {
                tolt_referral: window.tolt_referral,
              }),
            },

            ...(discount?.code
              ? { discountCode: discount.code }
              : discount?.id
                ? { discountId: discount.id }
                : {}),
            items: items,
            settings: {
              allowLogout: false,
            },
          });
        } catch (err) {
          console.error("Paddle.Checkout.open failed", err);
          setActivePaddleScreen(null);
          return;
        }

        /* Analytics */
        const product = PRICE_ID_TO_PLAN_MAP[priceId];
        const interval = PRICE_ID_TO_INTERVAL_MAP[priceId];
        trackEvent("Checkout Started", {
          plan: product,
          interval: interval,
          origin: origin,
          checkout_provider: "paddle",
          ...(isFounding ? { founding_offer: true } : {}),
        });
      };

      openWhenReady();
    },
    [
      origin,
      trackEvent,
      setActivePaddleScreen,
      discountVariant,
      intervalsVariant,
      bannerVariant,
    ]
  );

  const closeCheckout = useCallback(() => {
    if (isPaymentProcessing || !canCloseCheckout) {
      return;
    }
    // Always dismiss our overlay — even if Paddle never finished loading
    // (otherwise X is a no-op and the page looks stuck forever).
    const frameReady = !!document.querySelector(`.${PADDLE_TARGET_FRAME}`);
    if (frameReady && window.Paddle) {
      try {
        window.Paddle.Checkout.close();
      } catch {
        // ignore
      }
    }
    setOrigin(undefined);
    setActivePaddleScreen(null);
    unsubscribeFromPaddleEvents(eventsCallback);
  }, [
    canCloseCheckout,
    eventsCallback,
    isPaymentProcessing,
    setActivePaddleScreen,
    setOrigin,
    unsubscribeFromPaddleEvents,
  ]);

  const updateCheckout = useCallback(
    (checkout: UpdateCheckoutParams) => {
      if (!window.Paddle || !user) {
        return;
      }

      const updatedCheckout = {
        ...checkout,
        customer: {
          email: user.email,
        },
      };

      (window.Paddle as ExtendedPaddle).Checkout.updateCheckout(
        updatedCheckout
      );
    },
    [user]
  );

  const applyDiscountCode = useCallback(
    async (discountCode: string) => {
      if (!checkoutData || !window.Paddle || !user) {
        return {
          success: false,
        };
      }

      const priceIds = checkoutData.items.map((item) => item.price_id);

      const isValidDiscountCode = await checkIsValidDiscountCode({
        priceIds: priceIds,
        discountCode: discountCode,
      });

      if (!isValidDiscountCode) {
        return {
          success: false,
        };
      }

      const items = checkoutData.items.map((item) => {
        return {
          priceId: item.price_id,
          quantity: item.quantity,
        };
      });

      const customer = {
        email: user.email,
      };

      updateCheckout({
        customer,
        items,
        discountCode,
      });

      return {
        success: true,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkoutData, updateCheckout, user]
  );

  const updateNumberOfAdditionalVoices = useCallback(
    (numberOfVoices: number) => {
      if (!checkoutData || !window.Paddle || !user) {
        return;
      }

      setIsCheckoutUpdating(true);

      const customer = {
        email: user.email,
      };

      const discountCode = checkoutData.discount?.code;

      // Get the subscription plan checkout item
      const subscriptionPlanCheckoutItem = checkoutData.items.find((item) =>
        Object.values(ProductId).includes(item.product.id as TProductId)
      );

      // Get the price id of the additional voices item
      const additionalVoicePriceId =
        subscriptionPlanCheckoutItem?.billing_cycle?.interval ===
        SubscriptionIntervals.month
          ? (AdditionalVoicesId.voices_month as string)
          : (AdditionalVoicesId.voices_year as string);

      if (!subscriptionPlanCheckoutItem) {
        return;
      }

      // Create the items array with the subscription plan and additional voices items
      const items = [
        {
          priceId: subscriptionPlanCheckoutItem.price_id,
          quantity: subscriptionPlanCheckoutItem.quantity,
        },
        {
          priceId: additionalVoicePriceId,
          quantity: numberOfVoices,
        },
      ];

      // Filter out items with a quantity of 0
      const validatedItems = items.filter((item) => item.quantity > 0);

      // Update the checkout with the new items and discount code
      updateCheckout({
        customer,
        items: validatedItems,
        discountCode: discountCode ?? null,
      });
    },
    [checkoutData, setIsCheckoutUpdating, updateCheckout, user]
  );

  const removeDiscountCode = useCallback(() => {
    if (!checkoutData || !window.Paddle || !user) {
      return;
    }

    const discountCode = checkoutData.discount?.code;

    if (!discountCode) {
      return;
    }

    const items = checkoutData.items.map((item) => {
      return {
        priceId: item.price_id,
        quantity: item.quantity,
      };
    });

    const customer = {
      email: user.email,
    };

    updateCheckout({
      customer: customer,
      items: items,
      discountCode: null,
    });
  }, [checkoutData, updateCheckout, user]);

  return {
    startCheckout,
    closeCheckout,
    updateCheckout,
    applyDiscountCode,
    removeDiscountCode,
    updateNumberOfAdditionalVoices,
    isPaymentProcessing,
    eventsCallback,
    isCheckoutUpdating,
  };
};
